<template lang="pug">
.MegaCanvas(:style="{ cursor: $store.state.hover ? 'pointer' : 'auto' }")
</template>

<script>
import * as THREE from 'three'
import i18n from '../i18n'
import { mouse, range, lerp } from '@/js/utils.js'

export default {
  name: 'MegaCanvas',
  data () {
    return {
      scene: new THREE.Scene(),
      camera: new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2200),
      renderer: new THREE.WebGLRenderer(),
      raycaster: new THREE.Raycaster(),
      hoverCard: false,
      backgroundColor: new THREE.Color(this.$store.state.colors.accent),
      mousePos: new THREE.Vector2(0, 0),
      notLerpedMousePos: new THREE.Vector2(0, 0),
      lookat: new THREE.Vector3(0, 0, 0),
      moveCards: false
    }
  },
  methods: {
    animate () {
      this.notLerpedMousePos.setX(range(mouse.x, 0, window.innerWidth, -1, 1))
      this.notLerpedMousePos.setY(range(mouse.y, 0, window.innerHeight, 1, -1))
      this.mousePos.setX(lerp(this.mousePos.x, this.notLerpedMousePos.x, 0.08))
      this.mousePos.setY(lerp(this.mousePos.y, this.notLerpedMousePos.y, 0.08))

      this.raycaster.setFromCamera(this.mousePos, this.camera)

      const intersects = this.raycaster.intersectObjects(this.scene.children)
      this.hoverCard = intersects.length > 0 ? this.$store.state.cards.filter(card => card.mesh.id === intersects[0].object.id)[0] : false

      if (this.hoverCard) {
        this.camera.position.set(40 * this.mousePos.x, 40 * this.mousePos.y, lerp(this.camera.position.z, this.$store.state.loaderIsVisible ? 2000 : 25, 0.08))
        this.lookat.set(
          lerp(this.lookat.x, lerp(40 * this.notLerpedMousePos.x, this.hoverCard.mesh.position.x, 0.2), 0.1),
          lerp(this.lookat.y, lerp(40 * this.notLerpedMousePos.y, this.hoverCard.mesh.position.y, 0.2), 0.1),
          lerp(this.lookat.z, lerp(0, this.hoverCard.mesh.position.z, 0.2), 0.1)
        )
      } else {
        this.camera.position.set(40 * this.mousePos.x, 40 * this.mousePos.y, lerp(this.camera.position.z, this.$store.state.loaderIsVisible ? 2000 : 33, 0.08))
        this.lookat.set(
          lerp(this.lookat.x, 40 * this.notLerpedMousePos.x, 0.1),
          lerp(this.lookat.y, 40 * this.notLerpedMousePos.y, 0.1),
          lerp(this.lookat.z, 0, 0.1)
        )
      }

      if ((!this.$store.state.loaderIsVisible && !this.$store.state.inDarkPage) && !this.moveCards) setTimeout(() => { this.moveCards = true }, 1000)

      this.$store.state.cards.forEach((card, i) => {
        card.mesh.position.set(
          lerp(card.mesh.position.x, this.moveCards ? card.position.x : card.stackedPosition.x, 0.1),
          lerp(card.mesh.position.y, this.moveCards ? card.position.y : card.stackedPosition.y, 0.1),
          i % 2
        )
      })

      this.$store.state.decorations.forEach((decoration, i) => {
        decoration.mesh.position.set(
          lerp(decoration.mesh.position.x, this.moveCards ? decoration.position.x : decoration.stackedPosition.x, 0.1),
          lerp(decoration.mesh.position.y, this.moveCards ? decoration.position.y : decoration.stackedPosition.y, 0.1),
          -8 - (i % 2)
        )
      })

      this.camera.lookAt(this.lookat)

      this.scene.background = new THREE.Color(
        lerp(this.scene.background.r, this.backgroundColor.r, 0.1),
        lerp(this.scene.background.g, this.backgroundColor.g, 0.1),
        lerp(this.scene.background.b, this.backgroundColor.b, 0.1)
      )

      this.renderer.render(this.scene, this.camera)
      requestAnimationFrame(this.animate.bind(this))
    }
  },
  mounted () {
    this.$store.commit('setMegaCanvas', { camera: this.camera, renderer: this.renderer })

    this.camera.position.z = 2000
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.$el.appendChild(this.renderer.domElement)

    this.$store.state.cards.forEach(card => { this.scene.add(card.mesh) })
    this.$store.state.decorations.forEach(decoration => { this.scene.add(decoration.mesh) })

    this.scene.background = this.backgroundColor
    requestAnimationFrame(this.animate.bind(this))

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(window.innerWidth, window.innerHeight)
    })
  },
  watch: {
    hoverCard (newValue) {
      this.$store.commit('setCursorCTA', i18n.t('look_further'))
      this.$store.commit('hoverOnCard', newValue)
      if (this.$store.state.hoveredCard) {
        this.backgroundColor.set(newValue ? this.$store.state.colors[this.$store.state.hoveredCard.background] : this.$store.state.colors.accent)
      } else {
        this.backgroundColor.set(newValue ? this.$store.state.colors.dark : this.$store.state.colors.accent)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.MegaCanvas {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
}
</style>
